import { css } from 'styled-components';
import { MEDIA_QUERY } from '../../breakpoints/breakpoints';
import { PxToRem, PX_TO_REM, SIZES } from '../../sizes/sizes';
import { CSSProperties } from 'react';
import { EORA_NUMBERS } from '../../numbers/eoraNumbers';
import { EORA_SPACES, EORA_SPACES_STANDARD, EORA_SPACE_DEFAULT, useGutterMarginOrPaddingCss } from '../space';
import { IGapPropsBase, IGutterAsMarginPropsBase, useFlexDirectionCss } from '../spaceCommon';

export type EoraResponsiveSpaces = typeof EORA_SPACES_STANDARD;
export type EoraResponsiveSpace = EoraResponsiveSpaces[keyof EoraResponsiveSpaces];
type EoraResponsiveDevice = 'desktop' | 'mobile';

export interface IResponsiveGapProps extends IGapPropsBase, IResponsiveGapSizeProps {}

export interface IResponsiveGapSizeProps extends Pick<CSSProperties, 'alignItems'> {
  gap?: IAllowableResponsiveGutterProps;
}

export const eoraResponsiveSpaces: Record<EoraResponsiveDevice, Record<EoraResponsiveSpace, PxToRem>> = {
  desktop: {
    [EORA_SPACES.XSMALL]: PX_TO_REM[EORA_NUMBERS.DEVICES.DESKTOP.PADDING.SPACING_RESPONSIVE_XSMALL],
    [EORA_SPACES.SMALL]: PX_TO_REM[EORA_NUMBERS.DEVICES.DESKTOP.PADDING.SPACING_RESPONSIVE_SMALL],
    [EORA_SPACES.MEDIUM]: PX_TO_REM[EORA_NUMBERS.DEVICES.DESKTOP.PADDING.SPACING_RESPONSIVE_MEDIUM],
    [EORA_SPACES.LARGE]: PX_TO_REM[EORA_NUMBERS.DEVICES.DESKTOP.PADDING.SPACING_RESPONSIVE_LARGE],
    [EORA_SPACES.XLARGE]: PX_TO_REM[EORA_NUMBERS.DEVICES.DESKTOP.PADDING.SPACING_RESPONSIVE_XLARGE]
  },
  mobile: {
    [EORA_SPACES.XSMALL]: PX_TO_REM[EORA_NUMBERS.DEVICES.MOBILE.PADDING.SPACING_RESPONSIVE_XSMALL],
    [EORA_SPACES.SMALL]: PX_TO_REM[EORA_NUMBERS.DEVICES.MOBILE.PADDING.SPACING_RESPONSIVE_SMALL],
    [EORA_SPACES.MEDIUM]: PX_TO_REM[EORA_NUMBERS.DEVICES.MOBILE.PADDING.SPACING_RESPONSIVE_MEDIUM],
    [EORA_SPACES.LARGE]: PX_TO_REM[EORA_NUMBERS.DEVICES.MOBILE.PADDING.SPACING_RESPONSIVE_LARGE],
    [EORA_SPACES.XLARGE]: PX_TO_REM[EORA_NUMBERS.DEVICES.MOBILE.PADDING.SPACING_RESPONSIVE_XLARGE]
  }
};

export type IAllowableResponsiveGutterProps = boolean | EoraResponsiveSpace;
export interface IResponsiveGutterAsMarginProps extends IGutterAsMarginPropsBase<IAllowableResponsiveGutterProps> {}

export interface IResponsiveGutterProps extends IResponsiveGutterAsMarginProps {
  gutterAsMargin?: boolean;
}

export interface IResponsivePadding extends Partial<Record<'top' | 'right' | 'bottom' | 'left', EoraResponsiveSpace>> {}

const getResponsiveGutterSpace = (device: EoraResponsiveDevice, gutters?: IResponsiveGutterProps) => {
  const computeResponsiveSpace = (assignedSpace?: IAllowableResponsiveGutterProps, parentSpace?: IAllowableResponsiveGutterProps) => {
    if (assignedSpace === false) return `0`;
    if (assignedSpace === true) return eoraResponsiveSpaces[device][EORA_SPACE_DEFAULT];
    if (assignedSpace) return eoraResponsiveSpaces[device][assignedSpace];
    if (parentSpace === false) return `0`;
    if (parentSpace === true) return eoraResponsiveSpaces[device][EORA_SPACE_DEFAULT];
    if (parentSpace) return eoraResponsiveSpaces[device][parentSpace];
    return `0`;
  };
  const spaceTop = computeResponsiveSpace(gutters?.gutterTop, gutters?.gutter);
  const spaceRight = computeResponsiveSpace(gutters?.gutterRight, gutters?.gutter);
  const spaceBottom = computeResponsiveSpace(gutters?.gutterBottom, gutters?.gutter);
  const spaceLeft = computeResponsiveSpace(gutters?.gutterLeft, gutters?.gutter);
  return `${spaceTop} ${spaceRight} ${spaceBottom} ${spaceLeft}`;
};

const cascadeGap = (gap: EoraResponsiveSpace | true | undefined, space: EoraResponsiveSpace): EoraResponsiveSpace => {
  if (gap === true) return EORA_SPACE_DEFAULT;
  if (gap === undefined) return space;
  return gap;
};

const useResponsiveGapHelperCss = (space: EoraResponsiveSpace) => css<IResponsiveGapSizeProps>`
  display: flex;
  ${({ gap, theme }) => {
    return (
      gap !== false &&
      css`
        gap: ${eoraResponsiveSpaces.mobile[cascadeGap(gap, space)]};
        ${theme.mediaQueries[MEDIA_QUERY.MIN][SIZES.SMALL]} {
          gap: ${eoraResponsiveSpaces.desktop[cascadeGap(gap, space)]};
        }
      `
    );
  }};

  ${({ alignItems }) =>
    alignItems &&
    css`
      align-items: ${alignItems};
    `};
`;

const useResponsiveColumnGapCss = (space: EoraResponsiveSpace) => css<IResponsiveGapSizeProps>`
  flex-direction: column;
  ${useResponsiveGapHelperCss(space)}
`;

export const useResponsiveGutterCss = (asMargin?: boolean) => css<IResponsiveGutterProps>`
  ${({ gutter, gutterTop, gutterLeft, gutterBottom, gutterRight, theme }) => css`
    ${useGutterMarginOrPaddingCss(
      getResponsiveGutterSpace('mobile', {
        gutterTop,
        gutterLeft,
        gutterBottom,
        gutter,
        gutterRight
      }),
      asMargin
    )};
    ${theme.mediaQueries[MEDIA_QUERY.MIN][SIZES.SMALL]} {
      ${useGutterMarginOrPaddingCss(
        getResponsiveGutterSpace('desktop', {
          gutterTop,
          gutterLeft,
          gutterBottom,
          gutter,
          gutterRight
        }),
        asMargin
      )};
    }
  `}
`;

export const useResponsiveGapCss = (space: EoraResponsiveSpace = EORA_SPACE_DEFAULT) => css<IResponsiveGapProps>`
  ${({ column }) => (column ? useResponsiveColumnGapCss(space) : useResponsiveGapHelperCss(space))}
  ${useFlexDirectionCss()}
`;
